import * as Handlebars from 'handlebars';
import { DateTime } from 'luxon';
import {
  DATE_FORMATS,
  numberToCurrency,
  numberWithCommasDecimals,
} from '@admiin-com/ds-common';

type Maybe<T> = T | null | undefined;

type TaskDateValue = Maybe<string | number | Date>;

interface TaskEntityRef {
  name?: Maybe<string>;
  legalName?: Maybe<string>;
  entityType?: Maybe<string>;
}

interface TaskLike {
  to?: Maybe<TaskEntityRef>;
  toEntity?: Maybe<TaskEntityRef>;
  entityTo?: Maybe<TaskEntityRef>;
  entity?: Maybe<TaskEntityRef>;
  entityName?: Maybe<string>;
  entityLegalName?: Maybe<string>;
  settlementStatus?: Maybe<string>;
  amount?: Maybe<string | number>;
  settlementAmount?: Maybe<string | number>;
  futureLinkedTask?: Maybe<{
    amount?: Maybe<string | number>;
    dueAt?: TaskDateValue;
    dueDate?: TaskDateValue;
  }>;
  futureTaskInput?: Maybe<{
    amount?: Maybe<string | number>;
    dueAt?: TaskDateValue;
    dueDate?: TaskDateValue;
  }>;
  taxAmount?: Maybe<string | number>;
  total?: Maybe<string | number>;
  dueAt?: TaskDateValue;
  lodgementAt?: TaskDateValue;
  dueDate?: TaskDateValue;
  dueOn?: TaskDateValue;
  due?: TaskDateValue;
}

type TaskHelperInput = TaskLike | { task?: TaskHelperInput } | null | undefined;

const getFirstTruthyString = (values: Array<unknown>): string => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return '';
};

const resolveTask = (input: TaskHelperInput): TaskLike | null => {
  if (!input || typeof input !== 'object') {
    return null;
  }

  if (Array.isArray(input)) {
    return null;
  }

  const record = input as Record<string, unknown> & { task?: TaskHelperInput };

  if ('task' in record && record.task) {
    const nested = resolveTask(record.task);
    if (nested) {
      return nested;
    }
  }

  return record as TaskLike;
};

const normalizeTaskDate = (value: TaskDateValue): string => {
  if (!value) return '';

  if (value instanceof Date) {
    return DateTime.fromJSDate(value).toFormat('yyyy-MM-dd');
  }

  if (typeof value === 'number') {
    return DateTime.fromMillis(value).toFormat('yyyy-MM-dd');
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return '';

    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      return trimmed;
    }

    const isoParsed = DateTime.fromISO(trimmed);
    if (isoParsed.isValid) {
      return isoParsed.toFormat('yyyy-MM-dd');
    }

    const ausParsed = DateTime.fromFormat(trimmed, 'dd/MM/yyyy');
    if (ausParsed.isValid) {
      return ausParsed.toFormat('yyyy-MM-dd');
    }

    const candidateFormats = [
      DATE_FORMATS.FRONT_DATE,
      'dd MMM yyyy',
      'dd MMMM yyyy',
    ];

    for (const format of candidateFormats) {
      const parsed = DateTime.fromFormat(trimmed, format);
      if (parsed.isValid) {
        return parsed.toFormat('yyyy-MM-dd');
      }
    }
  }

  return '';
};

const getTaskDueDate = (taskInput: TaskHelperInput): string => {
  const task = resolveTask(taskInput);
  if (!task) return '';

  return normalizeTaskDate(
    task.dueAt ??
      task.lodgementAt ??
      task.dueDate ??
      task.dueOn ??
      task.due ??
      task.futureLinkedTask?.dueAt ??
      task.futureLinkedTask?.dueDate
  );
};

const getTaskSettlementAmount = (
  taskInput: TaskHelperInput
): string | number => {
  const task = resolveTask(taskInput);
  if (!task) return '';

  console.log('task: ', task);

  const candidates: Array<Maybe<string | number>> = [
    task.futureLinkedTask?.amount,
    task.futureTaskInput?.amount,
    task.amount,
    task.settlementAmount,
    task.taxAmount,
    task.total,
  ];

  for (const candidate of candidates) {
    if (candidate !== undefined && candidate !== null && candidate !== '') {
      return candidate;
    }
  }

  return '';
};

const getTaskEntityName = (taskInput: TaskHelperInput): string => {
  const task = resolveTask(taskInput);
  if (!task) return '';

  return getFirstTruthyString([
    task.to?.name,
    task.toEntity?.name,
    task.entityTo?.legalName,
    task.entityTo?.name,
    task.entity?.legalName,
    task.entity?.name,
    task.entityName,
    task.entityLegalName,
  ]);
};

const getTaskEntityType = (taskInput: TaskHelperInput): string => {
  const task = resolveTask(taskInput);
  if (!task) return '';

  return getFirstTruthyString([
    task.to?.entityType,
    task.toEntity?.entityType,
    task.entityTo?.entityType,
    task.entity?.entityType,
  ]);
};

const getTaskYear = (taskInput: TaskHelperInput): string => {
  const task = resolveTask(taskInput);
  if (!task) return '';
  const year = (task as Record<string, unknown>).year;
  if (year === undefined || year === null) return '';
  return String(year).trim();
};

const ENTITY_TYPE_ORDER = [
  'INDIVIDUAL',
  'SOLE_TRADER',
  'PARTNERSHIP',
  'COMPANY',
  'TRUST',
  'SELF_MANAGED_SUPER_FUND',
  'NOT_FOR_PROFIT',
];

const sortTasksByEntityType = (tasks: TaskHelperInput[]): TaskHelperInput[] => {
  if (!Array.isArray(tasks)) return [];

  return [...tasks].sort((a, b) => {
    const typeA = getTaskEntityType(a)
      .toUpperCase()
      .replace(/[_\s-]/g, '_');
    const typeB = getTaskEntityType(b)
      .toUpperCase()
      .replace(/[_\s-]/g, '_');

    const indexA = ENTITY_TYPE_ORDER.indexOf(typeA);
    const indexB = ENTITY_TYPE_ORDER.indexOf(typeB);

    // If both types are in the order array, sort by their position
    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }

    // If only one type is in the order array, it comes first
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;

    // If neither is in the order array, sort alphabetically
    return typeA.localeCompare(typeB);
  });
};

export const registerHandlebarsHelpers = (): void => {
  Handlebars.registerHelper('isEven', (index: number) => index % 2 === 0);
  Handlebars.registerHelper(
    'ifEquals',
    function (
      this: unknown,
      arg1: unknown,
      arg2: unknown,
      options: Handlebars.HelperOptions
    ) {
      return arg1 === arg2 ? options.fn(this) : options.inverse(this);
    }
  );
  Handlebars.registerHelper(
    'eq',
    (arg1: unknown, arg2: unknown) => arg1 === arg2
  );
  Handlebars.registerHelper('or', (...args: unknown[]) =>
    args.slice(0, Math.max(0, args.length - 1)).some(Boolean)
  );
  Handlebars.registerHelper('isPastDate', (date: string) => {
    if (!date) return false;
    return (
      DateTime.fromFormat(date, 'yyyy-MM-dd')
        .setZone('Australia/Sydney')
        .startOf('day') <
      DateTime.now().setZone('Australia/Sydney').startOf('day')
    );
  });
  Handlebars.registerHelper('isTodayDate', (date: string) => {
    if (!date) return false;
    return DateTime.fromFormat(date, 'yyyy-MM-dd')
      .setZone('Australia/Sydney')
      .startOf('day')
      .equals(DateTime.now().setZone('Australia/Sydney').startOf('day'));
  });
  Handlebars.registerHelper('formatCurrency', (value: unknown) => {
    if (typeof value === 'number') {
      return numberToCurrency(value);
    }
    if (typeof value === 'string') {
      const parsed = parseFloat(value);
      return numberToCurrency(Number.isFinite(parsed) ? parsed : 0);
    }

    const numeric = Number(value ?? 0);
    return numberToCurrency(Number.isFinite(numeric) ? numeric : 0);
  });
  const normalizeEntityType = (value: unknown) =>
    typeof value === 'string'
      ? value.replace(/[,\s_-]/g, '').toUpperCase()
      : '';
  Handlebars.registerHelper(
    'isEntityType',
    (value: unknown, expected: string) =>
      Boolean(value && expected) &&
      normalizeEntityType(value) === normalizeEntityType(expected)
  );
  const parseAmount = (amount: unknown) => {
    if (typeof amount === 'number') {
      return amount;
    }
    if (typeof amount === 'string') {
      const sanitized = amount.replace(/[^0-9.-]/g, '');
      const parsed = parseFloat(sanitized);
      return Number.isFinite(parsed) ? parsed : NaN;
    }
    const numeric = Number(amount ?? NaN);
    return Number.isFinite(numeric) ? numeric : NaN;
  };
  Handlebars.registerHelper(
    'formatTaskSettlementAmount',
    (amount: unknown, settlementStatus?: unknown) => {
      const normalizedStatus = (settlementStatus || '')
        .toString()
        .toUpperCase();
      console.log('amount: ', amount);
      const numericAmount = parseAmount(amount);
      console.log('numericAmount: ', numericAmount);

      if (
        normalizedStatus === 'NIL' ||
        !Number.isFinite(numericAmount) ||
        Math.abs(numericAmount) < 0.005
      ) {
        return 'NIL';
      }

      if (normalizedStatus === 'REFUNDABLE') {
        return `(${numberToCurrency(Math.abs(numericAmount))})`;
      }

      return numberToCurrency(numericAmount);
    }
  );
  Handlebars.registerHelper('taskEntityName', (task: TaskHelperInput) =>
    getTaskEntityName(task)
  );
  Handlebars.registerHelper('taskSettlementAmount', (task: TaskHelperInput) =>
    getTaskSettlementAmount(task)
  );
  Handlebars.registerHelper('taskDueDate', (task: TaskHelperInput) =>
    getTaskDueDate(task)
  );
  Handlebars.registerHelper('taskYear', (task: TaskHelperInput) =>
    getTaskYear(task)
  );
  Handlebars.registerHelper(
    'sortTasksByEntityType',
    (tasks: TaskHelperInput[]) => sortTasksByEntityType(tasks)
  );
  Handlebars.registerHelper('formatNumber', (value: unknown) => {
    let numeric: number;
    if (typeof value === 'number') {
      numeric = value;
    } else if (typeof value === 'string') {
      const parsed = parseFloat(value);
      numeric = Number.isFinite(parsed) ? parsed : 0;
    } else {
      const coerced = Number(value ?? 0);
      numeric = Number.isFinite(coerced) ? coerced : 0;
    }

    return numberWithCommasDecimals(numeric);
  });
  Handlebars.registerHelper(
    'formatDate',
    (value: string, format = DATE_FORMATS.USER_DATE) => {
      if (!value) return '';
      const isoRegex = /^\d{4}-\d{2}-\d{2}$/;
      const date = isoRegex.test(value)
        ? DateTime.fromFormat(value, DATE_FORMATS.BACKEND_DATE)
        : DateTime.fromISO(value);
      return date.isValid ? date.toFormat(format) : value;
    }
  );
  Handlebars.registerHelper('json', (value: unknown) => {
    try {
      return new Handlebars.SafeString(JSON.stringify(value, null, 2));
    } catch {
      return String(value);
    }
  });
};
